import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../api/client";

const defaultAnnouncement = {
  message: "",
  type: "general",
  beaconsEnabled: false,
};

const SNOWBANK_STORAGE_KEY = "snowbank-effect-enabled";

const AnnouncementContext = createContext({
  announcement: defaultAnnouncement,
  updateAnnouncement: () => {},
  clearAnnouncement: () => {},
  refreshAnnouncement: () => {},
  snowbankEnabled: false,
  toggleSnowbank: () => {},
});

export function AnnouncementProvider({ children }) {
  const [announcement, setAnnouncement] = useState(defaultAnnouncement);
  const [snowbankEnabled, setSnowbankEnabled] = useState(() => {
    if (typeof window === "undefined") return false;
    const saved = window.localStorage.getItem(SNOWBANK_STORAGE_KEY);
    return saved === "true";
  });

  const fetchAnnouncement = useCallback(async () => {
    try {
      const res = await api.get("/system-settings/announcement");
      if (res.data) {
        setAnnouncement({
          message: res.data.message ?? "",
          type: res.data.type ?? "general",
          beaconsEnabled: res.data.beaconsEnabled ?? false,
        });
      }
    } catch (error) {
      // Silently fail - user may not be authenticated yet
      console.warn("Failed to fetch announcement", error);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  // Poll every 30 seconds to keep in sync across clients
  useEffect(() => {
    const interval = setInterval(fetchAnnouncement, 30000);
    return () => clearInterval(interval);
  }, [fetchAnnouncement]);

  // Reset snowbank when beacons are disabled
  useEffect(() => {
    if (!announcement.beaconsEnabled && snowbankEnabled) {
      setSnowbankEnabled(false);
      window.localStorage.setItem(SNOWBANK_STORAGE_KEY, "false");
    }
  }, [announcement.beaconsEnabled, snowbankEnabled]);

  const toggleSnowbank = useCallback(() => {
    // Only allow toggle when beacons are enabled
    if (!announcement.beaconsEnabled) return;
    setSnowbankEnabled((prev) => {
      const newValue = !prev;
      window.localStorage.setItem(SNOWBANK_STORAGE_KEY, String(newValue));
      return newValue;
    });
  }, [announcement.beaconsEnabled]);

  const value = useMemo(
    () => ({
      announcement,
      updateAnnouncement: async (update) => {
        try {
          const payload = {
            message: update?.message ?? "",
            type: update?.type ?? "general",
            beaconsEnabled: update?.beaconsEnabled ?? false,
          };
          const res = await api.put("/system-settings/announcement", payload);
          setAnnouncement({
            message: res.data.message ?? "",
            type: res.data.type ?? "general",
            beaconsEnabled: res.data.beaconsEnabled ?? false,
          });
        } catch (error) {
          console.error("Failed to update announcement", error);
          throw error;
        }
      },
      clearAnnouncement: async () => {
        try {
          await api.delete("/system-settings/announcement");
          setAnnouncement(defaultAnnouncement);
        } catch (error) {
          console.error("Failed to clear announcement", error);
          throw error;
        }
      },
      refreshAnnouncement: fetchAnnouncement,
      snowbankEnabled: announcement.beaconsEnabled && snowbankEnabled,
      toggleSnowbank,
    }),
    [announcement, fetchAnnouncement, snowbankEnabled, toggleSnowbank]
  );

  return <AnnouncementContext.Provider value={value}>{children}</AnnouncementContext.Provider>;
}

export function useAnnouncement() {
  return useContext(AnnouncementContext);
}
